import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';
import { AvatarInput, PLATFORM_THEME_COLORS, Typography } from '@thinknet-gmbh/platform-common-ui';

import { useChangeEmployeeAvatar } from 'src/hooks/employees/change-employee-avatar.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';

import { displayAddress, generateReactKey } from 'src/common/helpers/common.helpers';
import { dateFormat } from '../../../common/helpers/date.helpers';
import { getCorrectFormatEmployeeInfoField } from '../employee-details.helpers';
import { PageTopbar } from 'src/components/employee/page-topbar';

import {
  PersonalInfoMainWrapper,
  TitleRowLabel,
  InfoBlockContainer,
  InfoBlock,
  StyledContainer,
  InfoElement,
} from '../employee-common.styles';

import { IEmployeeDetails } from 'src/components/employee/employee.interfaces';

const PersonalInfoView = ({
  employee,
  onClickEdit,
}: {
  employee: IEmployeeDetails;
  onClickEdit: () => void;
}) => {
  const { t } = useTranslation(['common', 'employee', 'settings']);
  const [avatar, setAvatar] = useState(employee?.profile?.avatarUrl);

  const { canEdit } = useUserCanManageDocument({
    createdBy: employee.createdBy,
    platformPermission: PLATFORM_PERMISSIONS.memberManagement,
  });
  const changeEmployeeAvatar = useChangeEmployeeAvatar();

  const onAvatarInputChange = (value: any) => {
    changeEmployeeAvatar({ variables: { memberId: employee._id, avatar: value } });
    setAvatar(value);
  };

  const { employeePersonalInfo, profile, emergencyContact, account } = employee;

  const {
    firstName,
    lastName,
    gender,
    phoneNumber,
    personalEmail,
    nationality,
    birthday,
    socials = {},
    salutation,
    address,
    companyPhoneNumber,
    countryOfBirth,
    cityOfBirth,
    birthName,
  } = profile || {};

  const { status, accessToPlatform, employeeNumber, academicDegree, internalNumber } =
    employeePersonalInfo || {};

  const {
    phoneNumber: emergencyPhoneNumber,
    relationship,
    name,
    childAllowance,
    maritalStatus,
    firstAider,
  } = emergencyContact || {};

  const { email, permissionGroup } = account || {};

  const contactData = {
    first: {
      companyPhoneNumber,
      email,
      xing: socials?.xing,
    },
    second: {
      personalPhoneNumber: phoneNumber,
      personalEmail,
      linkedIn: socials?.linkedIn,
    },
  };

  const contactDataArray = [contactData.first, contactData.second];

  const systemData = {
    first: {
      internalNumber,
      accessToPlatform: t(`common:labels.${accessToPlatform ? 'yes' : 'no'}`),
    },
    second: { employeeNumber },
    third: { status },
    fourth: { permissionType: permissionGroup },
  };

  const systemDataArray = [
    systemData.first,
    systemData.second,
    systemData.third,
    systemData.fourth,
  ];

  const emergencyData = {
    first: {
      emergencyName: name,
      emergencyRelationship: relationship,
    },
    second: {
      emergencyPhoneNumber,
      firstAider: t(`common:labels.${firstAider ? 'yes' : 'no'}`),
    },
  };

  const emergencyDataArray = [emergencyData.first, emergencyData.second];

  const familyData = {
    first: {
      maritalStatus,
    },
    second: {
      childAllowance,
    },
  };

  const familyDataArray = [familyData.first, familyData.second];

  return (
    <div>
      <PageTopbar
        onClickEdit={onClickEdit}
        createdBy={employee.createdBy}
        softTitle
        pageTitle={t(`employee:employeePersonalInfo.labels.personalInfo`)}
      />
      <AvatarInput
        size={64}
        squareCoverForm
        avatar={avatar}
        onChange={onAvatarInputChange}
        hasPermission={canEdit}
      />
      <PersonalInfoMainWrapper>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.personalData')}
          </TitleRowLabel>
          <InfoBlock>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.salutation`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {salutation
                    ? `${t(`employee:employeePersonalInfo.labels.salutationOptions.${salutation}`)}`
                    : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.firstName`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {firstName || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.lastName`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {lastName || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.gender`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {gender ? t(`employee:employeePersonalInfo.labels.${gender.toLowerCase()}`) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
          <InfoBlock columns={'1fr 3fr'}>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.academicDegree`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {academicDegree || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.homeAddress`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {displayAddress(address, t)}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
          <InfoBlock>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.birthday`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {birthday ? dateFormat(birthday) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.birthName`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {birthName || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.cityOfBirth`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {cityOfBirth || '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.countryOfBirth`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {countryOfBirth ? t(`common:country.${countryOfBirth}`, { defaultValue: countryOfBirth }) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
          <InfoBlock>
            <InfoBlockContainer>
              <InfoElement>
                <Typography weight="bold">
                  {`${t(`employee:employeePersonalInfo.labels.nationality`)}`}:
                </Typography>
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                  {nationality ? t(`common:country.${nationality}`, { defaultValue: nationality }) : '-'}
                </Typography>
              </InfoElement>
            </InfoBlockContainer>
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.contactData')}
          </TitleRowLabel>
          <InfoBlock columns={'1fr 1fr 1fr'}>
            {contactDataArray.map(el => {
              return (
                <InfoBlockContainer key={generateReactKey()}>
                  {Object.entries(el).map(([key, value]) => {
                    return (
                      <InfoElement key={generateReactKey()}>
                        <Typography weight="bold">{`${t(
                          `employee:employeePersonalInfo.labels.${key}`
                        )}`}</Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {getCorrectFormatEmployeeInfoField({ t, key, value }) || '-'}
                        </Typography>
                      </InfoElement>
                    );
                  })}
                </InfoBlockContainer>
              );
            })}
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.systemData')}
          </TitleRowLabel>
          <InfoBlock>
            {systemDataArray.map(el => {
              return (
                <InfoBlockContainer key={generateReactKey()}>
                  {Object.entries(el).map(([key, value]) => {
                    return (
                      <InfoElement key={generateReactKey()}>
                        <Typography weight="bold">
                          {`${t(`employee:employeePersonalInfo.labels.${key}`)}`}:
                        </Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {getCorrectFormatEmployeeInfoField({ t, key, value }) || '-'}
                        </Typography>
                      </InfoElement>
                    );
                  })}
                </InfoBlockContainer>
              );
            })}
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.emergencyContactDetails')}
          </TitleRowLabel>
          <InfoBlock columns={'1fr 1fr 1fr'}>
            {emergencyDataArray.map(el => {
              return (
                <InfoBlockContainer key={generateReactKey()}>
                  {Object.entries(el).map(([key, value]) => {
                    return (
                      <InfoElement key={generateReactKey()}>
                        <Typography weight="bold">
                          {`${t(`employee:emergencyDetails.labels.${key}`)}`}:
                        </Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {getCorrectFormatEmployeeInfoField({ t, key, value }) || '-'}
                        </Typography>
                      </InfoElement>
                    );
                  })}
                </InfoBlockContainer>
              );
            })}
          </InfoBlock>
        </StyledContainer>
        <StyledContainer>
          <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
            {t('employee:labels.familyStatus')}
          </TitleRowLabel>
          <InfoBlock columns={'1fr 1fr 1fr'}>
            {familyDataArray.map(el => {
              return (
                <InfoBlockContainer key={generateReactKey()}>
                  {Object.entries(el).map(([key, value]) => {
                    return (
                      <InfoElement key={generateReactKey()}>
                        <Typography weight="bold">
                          {`${t(`employee:emergencyDetails.labels.${key}`)}`}:
                        </Typography>
                        <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                          {getCorrectFormatEmployeeInfoField({ t, key, value }) || '-'}
                        </Typography>
                      </InfoElement>
                    );
                  })}
                </InfoBlockContainer>
              );
            })}
          </InfoBlock>
        </StyledContainer>
      </PersonalInfoMainWrapper>
    </div>
  );
};

export default PersonalInfoView;
